import { useMemo } from 'react';
import type Handsontable from 'handsontable';
import { useTableRbac, useFilteredContextMenu } from '../../../hooks/useTableRbac';
import { RbacStatusBadge } from '../../rbac/ReadOnlyBadge';
import type { UseTrackedTableRbacOptions, UseTrackedTableRbacResult } from './types';

export function useTrackedTableRbac(options: UseTrackedTableRbacOptions): UseTrackedTableRbacResult {
  const {
    rbacResource,
    rbacEnabled,
    showRbacBadge,
    columns,
    contextMenu,
    readOnly,
    title,
  } = options;

  const rbac = useTableRbac(rbacResource);
  const finalReadOnly = useMemo(() => {
    if (!rbacEnabled) {
      return readOnly ?? false;
    }
    if (readOnly === true) {
      return true;
    }
    return rbac.isReadOnly;
  }, [rbacEnabled, readOnly, rbac.isReadOnly]);

  const finalColumns = useMemo(() => {
    if (!finalReadOnly || !columns) {
      return columns;
    }
    return columns.map(col => ({
      ...col,
      readOnly: true,
    }));
  }, [columns, finalReadOnly]);

  const finalContextMenu = useFilteredContextMenu(
    contextMenu,
    rbacEnabled ? rbac : { canDeleteRows: true, isReadOnly: false } as typeof rbac
  );

  const titleWithRbacBadge = useMemo(() => {
    if (!showRbacBadge || !rbacEnabled || !rbac.hasResource) {
      return title;
    }
    
    if (!title) {
      return title;
    }

    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {title}
        <RbacStatusBadge 
          canWrite={rbac.canWrite} 
          canDelete={rbac.canDeleteRows}
          compact
        />
      </span>
    );
  }, [title, showRbacBadge, rbacEnabled, rbac.hasResource, rbac.canWrite, rbac.canDeleteRows]);

  return {
    finalReadOnly,
    finalColumns: finalColumns as Handsontable.ColumnSettings[] | undefined,
    finalContextMenu,
    titleWithRbacBadge,
    rbac: {
      isReadOnly: rbac.isReadOnly,
      canWrite: rbac.canWrite,
      canDeleteRows: rbac.canDeleteRows,
      hasResource: rbac.hasResource,
    },
  };
}






